import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import { AD_LAYOUT_STUBS } from "@consts";
import { getDateDataFromDateCode, newsArticleSchema } from "@utils";
import wordleMixin from "@/plugins/mixins/wordle";

dayjs.extend(utc);
dayjs.extend(tz);

export default {
	name: "WordleAnswers",
	mixins: [wordleMixin],
	async fetch() {
		this.isLoading = true;
		if (process.client) {
			this.loadData().then(() => {
				this.isLoading = false;
			});
		} else if (process.server) {
			this.serverDateParam = this.getDateParam();
			await this.loadData();
		}
	},
	mounted() {
		if (this.serverDateParam) {
			console.warn(
				"WORDLE DEBUG >>> found a serverDateParam value",
				this.serverDateParam
			);
			const clientDateParam = this.getDateParam();
			console.warn(
				"WORDLE DEBUG >>> creating client time",
				clientDateParam
			);
			if (clientDateParam !== this.serverDateParam) {
				console.warn(
					"WORDLE DEBUG >>> client time and server time mismatch",
					{ client: clientDateParam, server: this.serverDateParam }
				);
				this.isLoading = true;
				this.loadData().then(() => {
					this.isLoading = false;
				});
			} else {
				this.isLoading = false;
			}
		}
	},
	head({ $buildMetaDataObject }) {
		const metaData = $buildMetaDataObject({
			title: this.$t("WordleAnswersMetaTitle"),
			description: this.$t("WordleAnswersMetaDescription"),
		});

		metaData.script = metaData.script || [];

		metaData.script.push(
			newsArticleSchema({
				headline: metaData.title,
				image: "https://wordfinder.yourdictionary.com/static/images/wordle-answers-og.jpg",
				datePublished: "2022-01-19T08:00:00.000Z",
			})
		);

		return metaData;
	},
	asyncData({ res }) {
		if (res) {
			const expiryDate = dayjs()
				.utc()
				.hour(0)
				.minute(0)
				.startOf("minute")
				.add(1, "day");

			res.setHeader(
				"Expires",
				expiryDate.format("ddd, DD MMM YYYY HH:mm:ss [GMT]")
			);
		}
	},
	data() {
		return {
			serverDateParam: null,
			AD_LAYOUT_STUBS,
			isLoading: true,
			todayData: null,
			pastData: [],
			revealAnswer: false,
		};
	},
	computed: {
		tableHeaders() {
			if (this.isSmallDevice) {
				return ["Date", "Wordle #", "Answer"];
			}

			return ["Date", "Wordle Puzzle", "Wordle Answer"];
		},
		currentPuzzleNumber() {
			return this.todayData?.index?.toString() || "";
		},
		answers() {
			const data = {};

			if (this.todayData) {
				const todayMonthCode = this.todayData.date.substring(0, 4);
				const monthDate = getDateDataFromDateCode(
					todayMonthCode,
					this.isSmallDevice
				);

				data[todayMonthCode] = {
					headerText: `All ${monthDate?.formatted} Wordle Answers`,
					headerId: `answers-${monthDate.month}-${monthDate.year}`,
					rows: [
						{
							...this.todayData,
							date: getDateDataFromDateCode(
								this.todayData.date,
								this.isSmallDevice
							)?.formatted,
							isToday: true,
						},
					],
				};
			}

			if (!Array.isArray(this.pastData) || !this.pastData.length) {
				return data;
			}

			this.pastData.forEach((group) => {
				if (typeof data[group.month] !== "object") {
					const monthDate = getDateDataFromDateCode(
						group.month,
						this.isSmallDevice
					);

					data[group.month] = {
						headerText: `All ${monthDate?.formatted} Wordle Answers`,
						headerId: `answers-${monthDate.month}-${monthDate.year}`,
						rows: [],
					};
				}

				data[group.month].rows.push(
					...group.answers.map((answer) => ({
						...answer,
						date: getDateDataFromDateCode(
							answer.date,
							this.isSmallDevice
						)?.formatted,
					}))
				);
			});

			return Object.keys(data)
				.sort()
				.reverse()
				.map((key) => data[key]);
		},
		buttonText() {
			return this.isSmallDevice ? "Reveal" : "Reveal The Wordle";
		},
		todayDate() {
			const DATE_FORMAT = "en-US"; // MM-DD-YY
			const DATE_OPTIONS = {
				year: "numeric",
				month: "long",
				day: "2-digit",
			};

			const localeTime = new Date();

			return localeTime.toLocaleDateString(DATE_FORMAT, {
				...DATE_OPTIONS,
				...(process.server && { timeZone: "UTC" }),
			});
		},
	},
	methods: {
		loadData() {
			const params = {
				date: this.getDateParam(),
			};

			return new Promise((resolve, reject) => {
				this.$axios
					.get(`${this.$config.API_SERVICE}/v4/wordle`, { params })
					.then((response) => {
						if (response?.data?.status === 200) {
							const data = response?.data || {};
							this.todayData = data?.today || null;
							this.pastData = data?.past || [];
						}

						return resolve();
					})
					.catch((err) => {
						return reject(err);
					});
			});
		},
	},
};
